/**
 * 작성자 : 홍선영
 * 날짜 : 2024.08.08
 * 기능 : 본사/현장 위치 정보를 기반으로 시간/일간/주간/3일 날씨 정보를 리턴하는 훅
 */

import { constSelector, useRecoilValue } from 'recoil';
import { userState } from '../atoms';
import { apiGet } from '../services/_common';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import dayjs from 'dayjs';

const ONE_HOUR = 60 * 60 * 1000; // 60분

const useWeatherInfo = ({ autoRefresh = false }: { autoRefresh: boolean }) => {
  const userInfo = useRecoilValue(userState);
  const { hCd, sCd, gCd } = userInfo;
  const [geoInfo, setGeoInfo] = useState({ latitude: '', longitude: '', address: '', shortAdrs: '' });

  // 본사 정보 조회 쿼리
  const { data: headInfo } = useQuery([`headInfoGet_${hCd}`, hCd], () => fetchHeadInfo(), {
    enabled: !!hCd && (sCd === '' || sCd === '00000') && hCd.substring(0, 1).toLowerCase() === 'h',
    ...(autoRefresh && { refetchInterval: ONE_HOUR }),
  });

  // 현장 정보 조회 쿼리
  const { data: siteInfo } = useQuery([`siteInfoGet_${hCd}_${sCd}`, hCd, sCd], () => fetchSiteInfo(), {
    enabled: !!hCd && !!sCd && sCd !== '00000' && !(gCd === 'HM' && sCd !== '00000'),
    ...(autoRefresh && { refetchInterval: ONE_HOUR }),
  });

  // 사내 정보 조회 쿼리
  const { data: companyInfo } = useQuery([`companyInfoGet_${hCd}`, hCd], () => fetchCompanyInfo(), {
    enabled: !!hCd && (sCd === '' || sCd === '00000') && hCd.substring(0, 1).toLowerCase() === 'c',
    ...(autoRefresh && { refetchInterval: ONE_HOUR }),
  });

  // 일간 날씨 정보 조회 쿼리
  const {
    data: curWeather,
    refetch: refetchCurrentWeather,
    isError: isErrorCurrentWeather,
  } = useQuery([`currentDayWeatherGet_${hCd}_${sCd}`, geoInfo], () => fetchDayWeather(), {
    enabled: !!geoInfo.latitude && !!geoInfo.longitude && !!geoInfo.address,
    retry: 3,
    ...(autoRefresh && { refetchInterval: ONE_HOUR }),
  });

  // 주간 날씨 정보 조회 쿼리
  const {
    data: weekWeather,
    refetch: refetchWeekWeather,
    isError: isErrorWeekWeather,
  } = useQuery([`weekWeatherGet_${hCd}_${sCd}`, geoInfo], () => fetchWeekWeather(), {
    enabled: !!geoInfo.latitude && !!geoInfo.longitude && !!geoInfo.address,
    retry: 3,
    ...(autoRefresh && { refetchInterval: ONE_HOUR }),
  });

  // 3일 날씨 정보 조회 쿼리
  const {
    data: threeDaysWeather,
    refetch: refetchThreeDaysWeather,
    isError: isErrorThreeDaysWeather,
  } = useQuery([`threeDaysWeatherGet_${hCd}_${sCd}`, geoInfo], () => fetch3DaysWeather(), {
    enabled: !!geoInfo.latitude && !!geoInfo.longitude && !!geoInfo.address,
    retry: 3,
    ...(autoRefresh && { refetchInterval: ONE_HOUR }),
  });

  // 시간대별 날씨 정보 조회 쿼리
  const {
    data: timeWeather,
    refetch: refetchTimeWeather,
    isError: isErrorTimeWeather,
  } = useQuery([`timeWeatherGet_${hCd}_${sCd}`, geoInfo], () => fetchTimeWeather(), {
    enabled: !!geoInfo.latitude && !!geoInfo.longitude && !!geoInfo.address,
    retry: 3,
    ...(autoRefresh && { refetchInterval: ONE_HOUR }),
  });

  // 오픈일간 날씨 정보 조회 쿼리
  const {
    data: openDayWeather,
    refetch: refetchOpenDayWeather,
    isError: isErrorOpenDayWeather,
  } = useQuery([`openDayWeatherGet_${hCd}_${sCd}`, geoInfo], () => fetchOpenDayWeather(), {
    enabled: !!geoInfo.latitude && !!geoInfo.longitude && !!geoInfo.address,
    retry: 3,
    ...(autoRefresh && { refetchInterval: ONE_HOUR }),
  });

  // 오픈3일 날씨 정보 조회 쿼리
  const {
    data: openThreeDaysWeather,
    refetch: refetchOpenThreeDaysWeather,
    isError: isErrorOpenThreeDaysWeather,
  } = useQuery([`openThreeDaysWeatherGet_${hCd}_${sCd}`, geoInfo], () => fetchOpenThreeDaysWeather(), {
    enabled: !!geoInfo.latitude && !!geoInfo.longitude && !!geoInfo.address,
    retry: 3,
    ...(autoRefresh && { refetchInterval: ONE_HOUR }),
  });

  // 본사선택이 변경 될 때 geoInfo 상태 업데이트
  useEffect(() => {
    if (headInfo && (sCd === '' || sCd === '00000') && hCd.substring(0, 1).toLowerCase() === 'h') setGeoInfo(headInfo);
  }, [headInfo, hCd, sCd]);

  // 현장선택이 변경 될 때 geoInfo 상태 업데이트
  useEffect(() => {
    if (siteInfo) setGeoInfo(siteInfo);
  }, [siteInfo]);

  // 사내선택이 변경 될 때 geoInfo 상태 업데이트
  useEffect(() => {
    if (companyInfo) setGeoInfo(companyInfo);
  }, [companyInfo]);

  const fetchInfo = async (path: string, req: any, dataKey: string) => {
    try {
      const res = await apiGet({ path, req });
      const { latitude, longitude, address1 } = res.data.data[dataKey];
      const shortAdd = address1.split(' ');
      setGeoInfo({ latitude, longitude, address: address1, shortAdrs: shortAdd[1] });
      return { latitude, longitude, address: address1, shortAdrs: shortAdd[1] };
    } catch (error) {
      console.error('error', error);
      throw new Error('error');
    }
  };

  // 본사정보 데이터 조회
  const fetchHeadInfo = async () => {
    return fetchInfo('/head/info', { hCd }, 'headInfo');
  };

  // 현장정보 데이터 조회
  const fetchSiteInfo = async () => {
    return fetchInfo('/site/info', { hCd, sCd }, 'siteInfo');
  };

  const fetchCompanyInfo = async () => {
    return fetchInfo('/company/info/one', { cCd: hCd }, '0');
  };

  // 일간 날씨 데이터 조회
  const fetchDayWeather = async () => {
    try {
      const path = `/weather/day?latitude=${geoInfo.latitude}&longitude=${geoInfo.longitude}&address=${geoInfo.address}`;
      const res = await apiGet({ path });
      return res.data.data;
    } catch (error) {
      console.error('error', error);
      throw new Error('error');
    }
  };

  // 주간 날씨 데이터 조회
  const fetchWeekWeather = async () => {
    try {
      const path = `/weather/week?latitude=${geoInfo.latitude}&longitude=${geoInfo.longitude}&address=${geoInfo.address}`;
      const res = await apiGet({ path });
      const {
        morPop = '0',
        todayTmn = '0',
        todayTmx = '0',
        aftPop = '0',
        dinPop = '0',
        weekSky1 = '-',
        weekSky2 = '-',
        weekSky3 = '-',
        weekSky4 = '-',
        weekSky5 = '-',
        weekSky6 = '-',
        weekTmn1 = '0',
        weekTmn2 = '0',
        weekTmn3 = '0',
        weekTmn4 = '0',
        weekTmn5 = '0',
        weekTmn6 = '-',
        weekTmx1 = '0',
        weekTmx2 = '0',
        weekTmx3 = '0',
        weekTmx4 = '0',
        weekTmx5 = '0',
        weekTmx6 = '-',
      } = res.data.data;

      const status = getTimeStatus();
      //   setCurrentWeather((prev: any) => ({ ...prev, minTemp: todayTmn, maxTemp: todayTmx, rainPct: status === 'morning' ? morPop : status === 'evening' ? dinPop : aftPop }));

      // 6일 후 날씨 데이터를 로컬 스토리지에서 가져오기
      const after6DaysLocalStorage = window.localStorage.getItem('after6Days');
      const localAfter6Days = after6DaysLocalStorage ? JSON.parse(after6DaysLocalStorage) : days;
      const updatedDays = localAfter6Days.map((el: IDays, i: number) => {
        const targetDay = dayjs().add(i + 1, 'day'); // +1 to start from tomorrow
        return {
          ...el,
          date: targetDay.date(),
          day: targetDay.format('ddd'),
          sky: i === 0 ? weekSky1 : i === 1 ? weekSky2 : i === 2 ? weekSky3 : i === 3 ? weekSky4 : i === 4 ? weekSky5 : weekSky6,
          minTemp: i === 0 ? weekTmn1 : i === 1 ? weekTmn2 : i === 2 ? weekTmn3 : i === 3 ? weekTmn4 : i === 4 ? weekTmn5 : weekTmn6,
          maxTemp: i === 0 ? weekTmx1 : i === 1 ? weekTmx2 : i === 2 ? weekTmx3 : i === 3 ? weekTmx4 : i === 4 ? weekTmx5 : weekTmx6,
        };
      });
      return { week: updatedDays, morPop, todayTmn, todayTmx, aftPop, dinPop, rainPct: status === 'morning' ? morPop : status === 'evening' ? dinPop : aftPop };
    } catch (error) {
      console.error('error', error);
      throw new Error('error');
    }
  };

  // 3일 날씨 데이터 조회
  const fetch3DaysWeather = async () => {
    try {
      const path = `/weather/3days?latitude=${geoInfo.latitude}&longitude=${geoInfo.longitude}&address=${geoInfo.address}`;
      const res = await apiGet({ path });
      const {
        weekSky1 = '',
        weekSky2 = '',
        weekTmn1 = '',
        weekTmn2 = '',
        weekTmx1 = '',
        weekTmx2 = '',
        pm10 = '',
        todayTmn = '',
        todayTmx = '',
        morTmp = '',
        aftTmp = '',
        dinTmp = '',
        morPop = '',
        aftPop = '',
        dinPop = '',
      } = res.data.data;

      // 로컬 스토리지에서 2일 후 데이터 가져오기
      const after2DaysLocalStorage = window.localStorage.getItem('after2Days');
      const localAfter2Days = after2DaysLocalStorage ? JSON.parse(after2DaysLocalStorage) : days;

      const copyArray = [...localAfter2Days];
      const status = getTimeStatus();

      copyArray.map((el: any) => {
        if (el.order === 0) {
          copyArray[0] = {
            ...el,
            sky: weekSky1,
            minTemp: todayTmn,
            maxTemp: todayTmx,
            morTmp,
            aftTmp,
            dinTmp,
            pm10,
            rainPct: status === 'morning' ? morPop : status === 'evening' ? dinPop : aftPop,
          };
        }
        if (el.order === 1) {
          copyArray[1] = {
            ...el,
            sky: weekSky1,
            minTemp: weekTmn1,
            maxTemp: weekTmx1,
          };
        }
        if (el.order === 2) {
          copyArray[2] = {
            ...el,
            sky: weekSky2,
            minTemp: weekTmn2,
            maxTemp: weekTmx2,
          };
        }
      });
      return copyArray;
    } catch (error) {
      console.error('error', error);
      throw new Error('error');
    }
  };

  // 시간대별 날씨 데이터 조회
  const fetchTimeWeather = async () => {
    try {
      const path = `/weather/time?latitude=${geoInfo.latitude}&longitude=${geoInfo.longitude}&address=${geoInfo.address}`;
      const res = await apiGet({ path });
      const { pm10 = '', pm25 = '', weatherList = [] } = res.data.data;
      return { pm10, pm25, weatherList };
    } catch (error) {
      console.error('error', error);
      throw new Error('error');
    }
  };

  // 오픈 날씨 API - 일간 날씨 데이터 조회
  const fetchOpenDayWeather = async () => {
    try {
      const path = `/weather/day/open?latitude=${geoInfo.latitude}&longitude=${geoInfo.longitude}&address=${geoInfo.address}`;
      const res = await apiGet({ path });
      return res.data.data;
    } catch (error) {
      console.error('error', error);
      throw new Error('error');
    }
  };

  // 오픈 날씨 API - 3일 날씨 데이터 조회
  const fetchOpenThreeDaysWeather = async () => {
    try {
      const path = `/weather/3days/open?latitude=${geoInfo.latitude}&longitude=${geoInfo.longitude}&address=${geoInfo.address}`;
      const res = await apiGet({ path });
      const { daySky1 = '-', daySky2 = '-', dayTmn1 = '0', dayTmn2 = '0', dayTmx1 = '0', dayTmx2 = '0', todaySky = '-', todayTmn = '0', todayTmp = '0', todayTmx = '0' } = res.data.data;
      // 2일 후 데이터 가져오기
      const after2Days = Array.from({ length: 3 }).map((_, index) => {
        const targetDay = dayjs().add(index, 'day'); // 오늘일로부터 2일 후까지
        return {
          order: index,
          date: targetDay.date(),
          day: targetDay.format('ddd'),
          maxTemp: '0',
          minTemp: '0',
          sky: '-',
          wsd: '0',
          morTmp: '0',
          aftTmp: '0',
          dinTmp: '0',
          pm10: '0',
          rainPct: '0',
          tmp: '0',
        };
      });
      const copyArray = [...after2Days];

      copyArray.map((el: any) => {
        if (el.order === 0) {
          copyArray[0] = {
            ...el,
            sky: todaySky,
            tmp: todayTmp,
            minTemp: todayTmn,
            maxTemp: todayTmx,
          };
        }
        if (el.order === 1) {
          copyArray[1] = {
            ...el,
            sky: daySky1,
            minTemp: dayTmn1,
            maxTemp: dayTmx1,
          };
        }
        if (el.order === 2) {
          copyArray[2] = {
            ...el,
            sky: daySky2,
            minTemp: dayTmn2,
            maxTemp: dayTmx2,
          };
        }
      });
      return copyArray;
    } catch (error) {
      console.error('error', error);
      throw new Error('error');
    }
  };

  return {
    curWeather,
    weekWeather,
    threeDaysWeather,
    openDayWeather,
    openThreeDaysWeather,
    tWeather: timeWeather,
    shortAdrs: geoInfo.shortAdrs,
    refetchCurrentWeather,
    refetchWeekWeather,
    refetchThreeDaysWeather,
    refetchOpenDayWeather,
    refetchOpenThreeDaysWeather,
    refetchTimeWeather,
    isErrorCurrentWeather,
    isErrorWeekWeather,
    isErrorThreeDaysWeather,
    isErrorTimeWeather,
    isErrorOpenDayWeather,
    isErrorOpenThreeDaysWeather,
  };
};

export default useWeatherInfo;

const getTimeStatus = (): string => {
  const currentHour = dayjs().hour();
  if (currentHour >= 0 && currentHour < 6) return 'morning';
  if (currentHour >= 6 && currentHour < 18) return 'afternoon';
  return 'evening';
};

const days = Array.from({ length: 6 }).map((_, index) => {
  const targetDay = dayjs().add(index + 1, 'day'); // +1 to start from tomorrow
  return {
    order: index,
    date: targetDay.date(),
    day: targetDay.format('ddd'),
    maxTemp: '0',
    minTemp: '0',
    sky: '-',
  };
});
